import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { getAuthToken } from "../../../utils/getAuthToken";

// Calling has its own dedicated single-instance service — falls back to the
// main API server only if REACT_APP_CALLING_URL is unset (e.g. not yet configured).
const SERVER_DOMAIN = process.env.REACT_APP_CALLING_URL || process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

// Module-level cache shared by every mount of useCallingAppEnabled — CallButton
// (one per open contact chat) and CallManager's useLiveCallsFeed can all mount
// around the same time, and should share one /api/calling/app-status request
// instead of each firing their own. calling.appEnabled has no UI control (set
// directly in the WhatsappIntegration document, not exposed to end users/admins
// — see CallsSettings.jsx, which deliberately doesn't read or write it), so
// there's nothing in the client that needs to invalidate this early; the short
// TTL alone means a value changed at the DB is picked up within a minute of
// any open client's next check, same tradeoff as this app's other client-side
// caches (see inboxInfoSlice.js's contact cache).
const CACHE_TTL_MS = 60 * 1000;
let cachedValue = null; // { enabled: boolean, fetchedAt: number } | null
let inFlightPromise = null;

async function fetchAppEnabled() {
  if (cachedValue && Date.now() - cachedValue.fetchedAt < CACHE_TTL_MS) {
    return cachedValue.enabled;
  }
  if (inFlightPromise) return inFlightPromise;

  inFlightPromise = (async () => {
    try {
      const token = await getAuthToken();
      if (!token) return true; // fail open — same reasoning as getCallAppStatus server-side
      const res = await axios.get(`${SERVER_DOMAIN}/api/calling/app-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const enabled = res.data?.appEnabled !== false;
      cachedValue = { enabled, fetchedAt: Date.now() };
      return enabled;
    } catch (err) {
      return true; // fail open — a transient failure here shouldn't hide calling from an otherwise-entitled agent
    } finally {
      inFlightPromise = null;
    }
  })();

  return inFlightPromise;
}

/**
 * Cheap, shared "is calling turned on for this workspace at all" check —
 * gates useCallPermission's and useLiveCallsFeed's own requests so they never
 * fire when an admin has turned calling off locally (see
 * WhatsappIntegrationModel.js's calling.appEnabled and callingController.js's
 * getCallAppStatus). Defaults to enabled=true while the first check is still
 * in flight, same fail-open stance as the backend endpoint this calls — this
 * hook exists to skip unnecessary requests when calling is OFF, not to gate
 * anything while its own answer is still unknown.
 *
 * Also folds in the PLAN-level calling switch (a different axis from the
 * workspace toggle above — see LeftNav.jsx's "Calls" nav gate and
 * CallButton.jsx's own callingFeatureEnabled check, same featureKey/Redux
 * selector) — combined here, at the source, so every consumer of this hook
 * (useCallPermission's permission GET, useLiveCallsFeed) automatically skips
 * its own request the moment either the workspace or the plan has calling
 * off, with no changes needed in either consumer.
 */
export default function useCallingAppEnabled() {
  const [appEnabled, setAppEnabled] = useState(cachedValue ? cachedValue.enabled : true);
  const [loading, setLoading] = useState(!cachedValue);
  const subscriptionFeatures = useSelector((state) => state.subscription?.features);

  useEffect(() => {
    let cancelled = false;
    fetchAppEnabled().then((value) => {
      if (!cancelled) {
        setAppEnabled(value);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Same fail-open pattern as every other consumer of this Redux slice — don't gate on a
  // feature list that hasn't loaded yet. Checks both `enabled` and whether either direction
  // actually has capacity (limit > 0) — see LeftNav.jsx's matching comment for why `enabled`
  // alone isn't enough: a per-customer override can only ever zero the numeric limit, never
  // flip `enabled` itself, so an account deliberately zeroed out for calling would otherwise
  // still read as "enabled" here.
  const callMinutesInboundFeature = subscriptionFeatures?.find((f) => f.key === "callMinutesInbound");
  const callMinutesOutboundFeature = subscriptionFeatures?.find((f) => f.key === "callMinutesOutbound");
  const planCallingEnabled =
    subscriptionFeatures == null ||
    subscriptionFeatures.length === 0 ||
    (callMinutesOutboundFeature?.enabled !== false &&
      (Number(callMinutesInboundFeature?.limit || 0) > 0 || Number(callMinutesOutboundFeature?.limit || 0) > 0));

  return { enabled: appEnabled && planCallingEnabled, loading };
}
